import { type JSX } from 'react';

import type { Profile } from '@schema/profile.js';
import type { Tile } from '@schema/tiles.js';

import type { BuilderStore } from '../state.js';

import { SelectField, TextField } from './Field.js';
import {
  type AppearanceTile,
  TileAppearance,
  WebTileFields,
  type WebTileShape,
} from './TileGridEditor.js';

const MAX_FEATURED = 4;

// Note: the schema's theme.heroStyle field is preserved for backward
// compatibility with old configs but is ignored at render time — the
// renderer always shows the featured-tile band. We deliberately don't
// expose a picker here because it'd be a placebo control.

/**
 * Featured tile shape — narrower than the full Tile union because
 * the schema's `featuredTiles` refine rejects spacer + folder. Using
 * the inferred element type keeps TS strict at the boundary.
 */
type FeaturedTile = Profile['featuredTiles'][number];
type FeaturedTileType = FeaturedTile['type'];
const AGENT_ACTIONS = ['reboot', 'clearAppData', 'runScript', 'syncNow'] as const;

export interface FeaturedTilesEditorProps {
  store: BuilderStore;
}

/**
 * Editor for the hero band's featured tiles (up to 4). A simpler
 * cousin of TileGridEditor — no grid layout, no spacer or folder
 * variants (schema rejects them here), just a flat list with
 * add/remove buttons and per-row type / label / target fields.
 *
 * Icons and iconRef aren't editable here yet; admins set those in
 * the main TileGridEditor (Phase 6 surface). The hero falls back to
 * the generated letter avatar when no icon is set, which is fine
 * for the placeholder defaults that ship with the starter config.
 */
export function FeaturedTilesEditor({ store }: FeaturedTilesEditorProps): JSX.Element {
  const profile = store.activeProfile;
  const tiles = profile.featuredTiles;
  const atCapacity = tiles.length >= MAX_FEATURED;

  function update(nextTiles: FeaturedTile[]): void {
    store.patchProfile({ featuredTiles: nextTiles });
  }

  function addTile(): void {
    if (atCapacity) return;
    const next: FeaturedTile = {
      type: 'app',
      id: crypto.randomUUID(),
      label: `Feature App ${String(tiles.length + 1)}`,
      packageName: 'com.example.featured',
    };
    update([...tiles, next]);
  }

  function removeTile(id: string): void {
    update(tiles.filter((t) => t.id !== id));
  }

  function replaceTile(id: string, tile: FeaturedTile): void {
    update(tiles.map((t) => (t.id === id ? tile : t)));
  }

  function changeType(id: string, nextType: FeaturedTileType): void {
    const existing = tiles.find((t) => t.id === id);
    if (!existing) return;
    // Carry label across; reset type-specific fields to a sensible default.
    if (nextType === 'app') {
      replaceTile(id, {
        type: 'app',
        id,
        label: existing.label,
        packageName: 'com.example.featured',
      });
    } else if (nextType === 'web') {
      replaceTile(id, {
        type: 'web',
        id,
        label: existing.label,
        url: 'https://example.com',
        openExternal: false,
      });
    } else {
      replaceTile(id, { type: 'agentAction', id, label: existing.label, action: 'syncNow' });
    }
  }

  return (
    <div className="editor">
      <header className="editor__head">
        <h2 className="editor__title">Hero — featured tiles</h2>
        <p className="editor__sub">
          Up to {String(MAX_FEATURED)} tiles render in the launcher's hero
          band. When any are set they take over the band; only the hero
          background color and (Pro/Ent) wallpaper still apply behind
          them. Spacer + folder tiles aren't allowed here.
        </p>
      </header>

      {tiles.length === 0 && (
        <p className="editor__empty">No featured tiles yet — add up to four.</p>
      )}

      <ol className="featured-list">
        {tiles.map((tile, index) => (
          <li key={tile.id} className="featured-list__item">
            <header className="featured-list__head">
              <span className="featured-list__badge">{index + 1}</span>
              <button
                type="button"
                className="featured-list__remove"
                onClick={() => removeTile(tile.id)}
                aria-label={`Remove featured tile ${String(index + 1)}`}
              >
                Remove
              </button>
            </header>

            <SelectField<FeaturedTileType>
              label="Type"
              value={tileTypeOf(tile)}
              options={[
                { value: 'app', label: 'App' },
                { value: 'web', label: 'Web' },
                { value: 'agentAction', label: 'Agent action' },
              ]}
              onChange={(next) => changeType(tile.id, next)}
            />
            <TextField
              label="Label"
              value={tile.label}
              onChange={(v) => replaceTile(tile.id, { ...tile, label: v })}
            />
            {tile.type === 'app' && (
              <TextField
                label="Package name"
                value={tile.packageName}
                onChange={(v) => replaceTile(tile.id, { ...tile, packageName: v })}
                hint="Android app package name, e.g., com.android.chrome. Find it in Play Store URLs or via `pm list packages` on the device."
              />
            )}
            {tile.type === 'web' && (
              <WebTileFields
                tile={tile as WebTileShape}
                onChange={(next) => replaceTile(tile.id, next as FeaturedTile)}
              />
            )}
            {tile.type === 'agentAction' && (
              <SelectField<typeof AGENT_ACTIONS[number]>
                label="Action"
                value={tile.action}
                options={AGENT_ACTIONS.map((a) => ({ value: a, label: a }))}
                onChange={(next) => replaceTile(tile.id, { ...tile, action: next })}
              />
            )}

            {/*
              Same icon-source + show-label control as the main tile-grid
              editor. featuredTiles are NonFolderTile so the cast back to
              FeaturedTile is sound — all appearance fields live on TileBase,
              shared by every variant.
            */}
            <TileAppearance
              tile={tile as AppearanceTile}
              onChange={(next) => replaceTile(tile.id, next as FeaturedTile)}
            />
          </li>
        ))}
      </ol>

      <button
        type="button"
        className="featured-list__add"
        onClick={addTile}
        disabled={atCapacity}
        data-testid="featured-add"
      >
        {atCapacity ? `Maximum (${String(MAX_FEATURED)}) reached` : '+ Add featured tile'}
      </button>
    </div>
  );
}

function tileTypeOf(tile: FeaturedTile): FeaturedTileType {
  return tile.type;
}
